'use client';

import { maybe, Maybe } from '@passionware/monads';
import { Slot } from '@radix-ui/react-slot';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';
import { ComponentProps, ReactNode, useEffect, useRef, useState } from 'react';
import { cn } from 'v5/platform/dom/cn';
import { StopPropagation } from 'v5/platform/react/StopPropagation';
import { usePortalContainer } from 'v5/primitives/PortalContainer';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    light?: boolean;
  }
>(({ className, sideOffset = 4, light, ...props }, ref) => (
  <TooltipPrimitive.Portal container={usePortalContainer()}>
    <StopPropagation>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'z-50 w-fit rounded-md px-3 py-1.5 text-xs text-balance shadow-sm outline-none',
          'origin-[var(--radix-tooltip-content-transform-origin)]',
          'data-[state=open]:animate-in data-[state=delayed-open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=delayed-open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=delayed-open]:zoom-in-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
          'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          light
            ? 'overflow-hidden border border-slate-200 bg-white/95 text-slate-950 shadow-md backdrop-blur-xs dark:border-slate-800 dark:bg-slate-950 dark:text-slate-50'
            : 'border border-slate-800/70 bg-slate-950/80 text-slate-50 shadow-black/30 backdrop-blur-xs dark:border-slate-800 dark:bg-slate-950 dark:text-slate-50',
          className
        )}
        {...props}
      />
    </StopPropagation>
  </TooltipPrimitive.Portal>
));

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };

export type SimpleTooltipProps = TooltipPrimitive.TooltipProps & {
  title: ReactNode;
  light?: boolean;
  align?: ComponentProps<typeof TooltipContent>['align'];
  side?: ComponentProps<typeof TooltipContent>['side'];
  contentClassName?: string;
};

export const SimpleTooltip = React.forwardRef<
  React.ElementRef<typeof TooltipTrigger>,
  SimpleTooltipProps & { children: React.ReactNode }
>(function SimpleTooltip(
  {
    children,
    open,
    title,
    delayDuration = 700,
    light,
    align = 'center',
    side = 'top',
    contentClassName,
    ...rest
  },
  ref
) {
  return (
    <Tooltip
      open={maybe.getOrElse(open, title ? undefined : false)}
      delayDuration={delayDuration}
      {...rest}
    >
      <TooltipTrigger asChild ref={ref}>
        {children}
      </TooltipTrigger>

      <TooltipContent
        align={align}
        side={side}
        light={light}
        className={cn('max-w-96 break-words', contentClassName)}
      >
        {title}
      </TooltipContent>
    </Tooltip>
  );
});

export interface SwitchableTooltipProps {
  tooltipChildren: ReactNode;
  children: ReactNode;
  enabled: Maybe<boolean>;
  title: ReactNode;
}

export function SwitchableTooltip({
  tooltipChildren,
  children,
  enabled,
  title
}: SwitchableTooltipProps) {
  if (enabled) {
    return <SimpleTooltip title={title}>{tooltipChildren}</SimpleTooltip>;
  }
  return <>{children}</>;
}

export function OverflowTooltip({
  children,
  title,
  ...rest
}: SimpleTooltipProps) {
  const triggerRef = useRef<HTMLElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const element = triggerRef.current;
    if (element) {
      const isHorizontalOverflow = element.scrollWidth > element.clientWidth;
      const isVerticalOverflow = element.scrollHeight > element.clientHeight;
      setIsOverflowing(isHorizontalOverflow || isVerticalOverflow);
    }
  }, [children]);

  return (
    <SimpleTooltip
      title={title}
      open={isOverflowing ? undefined : false}
      {...rest}
    >
      <Slot className="cursor-default" ref={triggerRef}>
        {children}
      </Slot>
    </SimpleTooltip>
  );
}
